import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import axios from 'axios';
import api from './axiosConfig';
import Login from './Login';

function App() {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [userGuid, setUserGuid] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [messageQueue, setMessageQueue] = useState([]);
  const [isProcessingMessage, setIsProcessingMessage] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressStep, setProgressStep] = useState(0);
  const [isProcessingScenario, setIsProcessingScenario] = useState(false);
  const [scenarioRunning, setScenarioRunning] = useState('no');
  const [responseTitle, setResponseTitle] = useState('');

  // Map progress messages to steps
  const progressSteps = [
    "Analyzing your request...",
    "Preparing information for your plan...",
    "Generating tailored insights based on your data...",
    "Refining considerations and action steps...",
    "Consolidating key guidance and recommendations...",
    "Verifying accuracy and relevance..."
  ];

  // Function to update progress message and step
  const updateProgress = (message) => {
    setProgressMessage(message);
    const stepIndex = progressSteps.findIndex(step => step === message);
    if (stepIndex !== -1) {
      setProgressStep(stepIndex);
    }
  };
  const [typingDelay] = useState(20);
  const [scenarioInputs, setScenarioInputs] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'tabs'
  const [activeTab, setActiveTab] = useState(0);
  const [recommendationHistory, setRecommendationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyViewMode, setHistoryViewMode] = useState('grid'); // 'grid' or 'tabs'
  const [activeHistoryTab, setActiveHistoryTab] = useState(0);
  const [activeScenarioGroup, setActiveScenarioGroup] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedScenarios, setExpandedScenarios] = useState(new Set());
  const [groupsInitialized, setGroupsInitialized] = useState(false);
  const [isNewScenarioGroup, setIsNewScenarioGroup] = useState(false);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const [forceLandingPage, setForceLandingPage] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileInfo, setProfileInfo] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [speechLanguage, setSpeechLanguage] = useState('en-US');
  const [sourcesModal, setSourcesModal] = useState({ show: false, sources: [] });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userFirstName, setUserFirstName] = useState('');
  const [isViewingAnotherUser, setIsViewingAnotherUser] = useState(false);
  const chatMessagesRef = useRef(null);
  const chatInputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Available languages for speech recognition
  const availableLanguages = [
    { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
    { code: 'en-GB', name: 'English (UK)', flag: '🇬🇧' },
    { code: 'es-ES', name: 'Spanish (Spain)', flag: '🇪🇸' },
    { code: 'es-MX', name: 'Spanish (Mexico)', flag: '🇲🇽' },
    { code: 'fr-FR', name: 'French (France)', flag: '🇫🇷' },
    { code: 'de-DE', name: 'German (Germany)', flag: '🇩🇪' },
    { code: 'it-IT', name: 'Italian (Italy)', flag: '🇮🇹' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)', flag: '🇧🇷' },
    { code: 'ru-RU', name: 'Russian (Russia)', flag: '🇷🇺' },
    { code: 'ja-JP', name: 'Japanese (Japan)', flag: '🇯🇵' },
    { code: 'ko-KR', name: 'Korean (Korea)', flag: '🇰🇷' },
    { code: 'zh-CN', name: 'Chinese (China)', flag: '🇨🇳' },
    { code: 'zh-TW', name: 'Chinese (Taiwan)', flag: '🇹🇼' },
    { code: 'yue-HK', name: 'Cantonese (Hong Kong)', flag: '🇭🇰' },
    { code: 'ar-SA', name: 'Arabic (Saudi Arabia)', flag: '🇸🇦' },
    { code: 'hi-IN', name: 'Hindi (India)', flag: '🇮🇳' },
    { code: 'nl-NL', name: 'Dutch (Netherlands)', flag: '🇳🇱' }
  ];

  // All available preset scenarios for quick start
  const allPresetScenarios = [
    {
      id: 'elderly-care',
      title: 'Elderly Parent Care',
      description: 'Planning care for aging parents',
      prompt: 'I need help planning care for my elderly parents who are starting to need more assistance with daily activities.'
    },
    {
      id: 'dementia-care',
      title: 'Dementia Care Planning',
      description: 'Managing care for someone with dementia',
      prompt: 'My family member has been diagnosed with dementia and I need to plan their long-term care and support.'
    },
    {
      id: 'disability-care',
      title: 'Disability Support',
      description: 'Supporting a family member with disabilities',
      prompt: 'I\'m caring for a family member with disabilities and need help understanding available resources and care options.'
    },
    {
      id: 'end-of-life',
      title: 'End-of-Life Planning',
      description: 'Preparing for end-of-life care decisions',
      prompt: 'I need guidance on end-of-life care planning and making difficult decisions about my loved one\'s care.'
    },
    {
      id: 'chronic-illness',
      title: 'Chronic Illness Management',
      description: 'Caring for someone with chronic conditions',
      prompt: 'I\'m helping manage care for a family member with chronic illness and need guidance on long-term planning.'
    },
    {
      id: 'recovery-support',
      title: 'Recovery Support',
      description: 'Supporting recovery from injury or surgery',
      prompt: 'My family member is recovering from an injury or surgery and I need help planning their care and rehabilitation.'
    },
    {
      id: 'mental-health',
      title: 'Mental Health Support',
      description: 'Supporting mental health challenges',
      prompt: 'I\'m caring for someone with mental health challenges and need help understanding care options and resources.'
    },
    {
      id: 'mobility-issues',
      title: 'Mobility Challenges',
      description: 'Managing mobility and accessibility needs',
      prompt: 'My family member has mobility issues and I need help planning for accessibility and daily care needs.'
    },
    {
      id: 'medication-management',
      title: 'Medication Management',
      description: 'Managing complex medication needs',
      prompt: 'I need help managing complex medication schedules and ensuring proper care for my family member.'
    },
    {
      id: 'home-care',
      title: 'Home Care Planning',
      description: 'Setting up home-based care services',
      prompt: 'I want to set up home care services for my family member and need guidance on options and planning.'
    },
    {
      id: 'respite-care',
      title: 'Respite Care',
      description: 'Finding temporary care relief',
      prompt: 'I need temporary care relief and want to explore respite care options for my family member.'
    },
    {
      id: 'financial-planning',
      title: 'Financial Planning',
      description: 'Managing care costs and insurance',
      prompt: 'I need help understanding the financial aspects of caregiving and planning for long-term costs.'
    },
    {
      id: 'legal-planning',
      title: 'Legal Planning',
      description: 'Legal documents and decision-making',
      prompt: 'I need guidance on legal planning including power of attorney and advance directives for my family member.'
    },
    {
      id: 'technology-support',
      title: 'Technology Support',
      description: 'Using technology for care management',
      prompt: 'I want to explore technology solutions to help manage my family member\'s care and safety.'
    },
    {
      id: 'transportation',
      title: 'Transportation Needs',
      description: 'Managing transportation and mobility',
      prompt: 'My family member has transportation challenges and I need help finding solutions for medical appointments and daily needs.'
    },
    {
      id: 'nutrition-care',
      title: 'Nutrition & Diet',
      description: 'Managing dietary and nutrition needs',
      prompt: 'I need help planning proper nutrition and dietary care for my family member\'s specific health needs.'
    },
    {
      id: 'social-engagement',
      title: 'Social Engagement',
      description: 'Maintaining social connections and activities',
      prompt: 'I want to help my family member stay socially engaged and active despite their care needs.'
    },
    {
      id: 'emergency-planning',
      title: 'Emergency Planning',
      description: 'Preparing for medical emergencies',
      prompt: 'I need to create an emergency plan and prepare for potential medical crises with my family member.'
    },
    {
      id: 'caregiver-burnout',
      title: 'Caregiver Support',
      description: 'Managing caregiver stress and burnout',
      prompt: 'I\'m feeling overwhelmed as a caregiver and need help managing stress and finding support resources.'
    },
    {
      id: 'transition-planning',
      title: 'Care Transitions',
      description: 'Managing changes in care needs',
      prompt: 'My family member\'s care needs are changing and I need help planning for transitions in their care.'
    }
  ];

  // Randomly select 4 scenarios to display
  const getRandomScenarios = () => {
    const shuffled = [...allPresetScenarios].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4);
  };

  const [presetScenarios, setPresetScenarios] = useState([]);

  // Initialize random scenarios on component mount
  useEffect(() => {
    setPresetScenarios(getRandomScenarios());
  }, []);

  // Check for existing login state on component mount
  useEffect(() => {
    const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const firstName = localStorage.getItem('userFirstName') || '';
    const userGuid = localStorage.getItem('user_guid') || '';
    const jwtToken = localStorage.getItem('jwt_token');
    
    // Only set as logged in if we have both login state and JWT token
    if (loggedIn && firstName && jwtToken) {
      setIsLoggedIn(true);
      setUserFirstName(firstName);
      if (userGuid) {
        setUserGuid(userGuid);
        // Check scenario_running status on page load if already logged in
        checkScenarioStatus(userGuid);
      }
    } else if (loggedIn && !jwtToken) {
      // Clear invalid login state if no JWT token, but keep user_guid for next login
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userFirstName');
      // Keep user_guid in localStorage for next login
    }
  }, []);

  // Handle successful login
  const handleLoginSuccess = (firstName, userGuid) => {
    setIsLoggedIn(true);
    setUserFirstName(firstName);
    if (userGuid) {
      setUserGuid(userGuid);
      // Load data immediately after login
      loadChatHistory(userGuid);
      loadRecommendationHistory(userGuid);
      loadProfileInfo(userGuid);
      // Check scenario_running status on login
      checkScenarioStatus(userGuid);
    }
  };

  // Handle logout
  const handleLogout = () => {
    // Only remove JWT token and login state, keep userGuid and other data
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userFirstName');
    // Keep user_guid in localStorage for next login
    setIsLoggedIn(false);
    setUserFirstName('');
    // Keep userGuid in state for data loading
    setMessages([]);
    setResults(null);
    setProfileInfo('');
    setRecommendationHistory([]);
  };


  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = speechLanguage;
      
      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setUserInput(prevInput => {
          // If there's existing text, add a space before the new transcript
          return prevInput ? `${prevInput} ${transcript}` : transcript;
        });
        // Don't stop listening automatically - let user control it
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        // Handle specific error cases
        if (event.error === 'not-allowed') {
          alert('Microphone access denied. Please allow microphone access to use voice input.');
          setIsListening(false);
        } else if (event.error === 'no-speech') {
          console.log('No speech detected, continuing to listen...');
          // Don't stop listening for no-speech errors
        } else if (event.error === 'audio-capture') {
          alert('No microphone found. Please check your microphone connection.');
          setIsListening(false);
        } else if (event.error === 'network') {
          console.log('Network error, trying to restart...');
          // Try to restart after a brief delay
          setTimeout(() => {
            if (isListening) {
              try {
                recognitionRef.current.start();
              } catch (error) {
                console.error('Error restarting after network error:', error);
                setIsListening(false);
              }
            }
          }, 1000);
        } else {
          // For other errors, stop listening
          setIsListening(false);
        }
      };
      
      recognitionRef.current.onend = () => {
        // Only stop listening if user explicitly stopped it
        // If recognition ended due to silence/timeout, restart it
        if (isListening) {
          console.log('Speech recognition ended, restarting...');
          try {
            recognitionRef.current.start();
          } catch (error) {
            console.error('Error restarting speech recognition:', error);
            setIsListening(false);
          }
        }
      };
      
      setSpeechSupported(true);
    } else {
      console.log('❌ Speech recognition not supported in this browser');
    }
  }, []);

  // Update speech recognition language when it changes
  useEffect(() => {
    if (recognitionRef.current && speechSupported) {
      recognitionRef.current.lang = speechLanguage;
      
      // Test if the language is actually supported
      if (recognitionRef.current.lang !== speechLanguage) {
        console.warn('⚠️ Language may not be supported. Requested:', speechLanguage, 'Actual:', recognitionRef.current.lang);
      }
    }
  }, [speechLanguage, speechSupported]);

  // Get user GUID from URL parameters or localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for delete parameter
    if (urlParams.get('delete') === '1') {
      // Load profile info from current user before clearing
      const currentUserGuid = userGuid || localStorage.getItem('user_guid');
      if (currentUserGuid && currentUserGuid !== 'null' && currentUserGuid !== '') {
        loadProfileInfo(currentUserGuid);
      }
      
      // Preserve profile info before clearing user data
      const currentProfileInfo = profileInfo;
      localStorage.removeItem('user_guid');
      setUserGuid(null);
      setRecommendationHistory([]);
      setMessages([]);
      // Keep profile info for potential reuse
      if (currentProfileInfo) {
        setProfileInfo(currentProfileInfo);
      }
      // Remove the delete parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    let guid = urlParams.get('user') || urlParams.get('user_guid') || urlParams.get('guid');
    
    // If no GUID in URL, check localStorage
    if (!guid) {
      guid = localStorage.getItem('user_guid');
    }
    
    // If still no GUID, let the backend generate one (don't create one in frontend)
    if (!guid) {
      setUserGuid(null); // Set to null so backend knows to generate one
    } else {
      // Save to localStorage for future use
      localStorage.setItem('user_guid', guid);
      setUserGuid(guid);
      // Load chat history, recommendation history, and profile for this user
      loadChatHistory(guid);
      loadRecommendationHistory(guid);
      loadProfileInfo(guid);
    }
  }, []);

  // Load recommendation history, chat history, and profile when userGuid changes (including on refresh)
  useEffect(() => {
    // Check URL parameter first for viewing another user
    const urlParams = new URLSearchParams(window.location.search);
    const userParam = urlParams.get('user');
    const targetUserGuid = userParam || userGuid;
    
    // Set viewing state
    setIsViewingAnotherUser(!!userParam);
    
    if (targetUserGuid && targetUserGuid !== 'null' && targetUserGuid !== '') {
      loadChatHistory(targetUserGuid);
      loadRecommendationHistory(targetUserGuid);
      loadProfileInfo(targetUserGuid);
      checkScenarioStatus(targetUserGuid);
    }
  }, [userGuid]);

  // Show conversation view based on data availability
  useEffect(() => {
    if (userGuid && userGuid !== 'null' && userGuid !== '') {
      // If forceLandingPage is true, always show landing page
      if (forceLandingPage) {
        setHasStartedConversation(false);
        // Check scenario status when on landing page
        checkScenarioStatus(userGuid);
        return;
      }
      
      // Show conversation view if we have messages, recommendations, or history
      if (messages.length > 0 || recommendationHistory.length > 0 || results) {
        setHasStartedConversation(true);
      }
    }
  }, [userGuid, messages, recommendationHistory, results, forceLandingPage]);


  // Set focus on chat input when on initial screen
  useEffect(() => {
    if (!hasStartedConversation && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [hasStartedConversation]);

  // Auto-scroll chat to bottom when messages change
  useEffect(() => {
    if (chatMessagesRef.current) {
      // Add small delay to ensure DOM has updated
      setTimeout(() => {
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages]);

  // Auto-scroll chat to bottom when processing message appears/disappears
  useEffect(() => {
    if (chatMessagesRef.current) {
      // Add small delay to ensure DOM has updated
      setTimeout(() => {
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [scenarioRunning, isProcessingScenario]);

  // Initialize all groups as expanded when recommendation history is first loaded
  useEffect(() => {
    if (recommendationHistory.length > 0 && !groupsInitialized) {
      // Check if we have the new grouped structure
      if (recommendationHistory[0].scenario_group_id) {
        // Create a set with all group indices expanded
        const allGroupsExpanded = new Set(recommendationHistory.map((_, index) => index));
        setExpandedGroups(allGroupsExpanded);
      }
      setGroupsInitialized(true);
    }
  }, [recommendationHistory, groupsInitialized]);

  // Process message queue
  useEffect(() => {
    if (!isProcessingMessage && messageQueue.length > 0) {
      processMessageQueue();
    }
  }, [isProcessingMessage, messageQueue]);

  // Save chat history whenever messages change (but not on initial load)
  useEffect(() => {
    if (messages.length > 0) {
      // Debounce the save operation to avoid too many API calls
      const timeoutId = setTimeout(() => {
        saveChatHistory(userGuid, messages);
      }, 1000); // Save 1 second after last message change
      
      return () => clearTimeout(timeoutId);
    }
  }, [messages, userGuid]);

  // Reset activeTab when new results come in
  useEffect(() => {
    if (results && results.outcomes && results.outcomes.merged_scenarios) {
      setActiveTab(0); // Reset to first tab when new results arrive
    }
  }, [results]);




  const processMessageQueue = () => {
    if (isProcessingMessage || messageQueue.length === 0) return;
    
    setIsProcessingMessage(true);
    const { type, content, timestamp } = messageQueue[0];
    setMessageQueue(prev => prev.slice(1));
    
    processSingleMessage(type, content, timestamp);
  };

     const processSingleMessage = (type, content, timestamp) => {
     const newMessage = {
       id: Date.now(),
       type,
       content: '',
       timestamp,
       isTyping: type === 'assistant'
     };

     setMessages(prev => [...prev, newMessage]);

     if (type === 'assistant') {
       // Use typewriter effect for assistant messages
       typeText(newMessage.id, content, () => {
         setIsProcessingMessage(false);
         // Set focus back to chat input after assistant message is complete
         if (chatInputRef.current) {
           chatInputRef.current.focus();
         }
       });
     } else {
       // For user messages, set content immediately
       setMessages(prev => prev.map(msg => 
         msg.id === newMessage.id ? { ...msg, content, isTyping: false } : msg
       ));
       setIsProcessingMessage(false);
     }
   };

  const typeText = (messageId, text, callback) => {
    let i = 0;
    
    const typeChar = () => {
      if (i < text.length) {
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, content: text.substring(0, i + 1) }
            : msg
        ));
        i++;
        
        // Scroll to bottom every 5 characters
        if (i % 5 === 0 && chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
        
        setTimeout(typeChar, typingDelay);
      } else {
        // Finish typing
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, content: text, isTyping: false }
            : msg
        ));
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
        if (callback) callback();
      }
    };
    
    typeChar();
  };

  const addMessageToQueue = (type, content) => {
    // Format timestamp for display (use current time)
    const displayTimestamp = formatTimestamp();
    setMessageQueue(prev => [...prev, { type, content, timestamp: displayTimestamp }]);
  };

  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Display timezone information (for debugging/verification)
  useEffect(() => {
    console.log('Detected timezone:', userTimezone);
  }, [userTimezone]);
  
  // Helper function to format timestamp with date and time for display
  const formatTimestamp = (date = null) => {
    // If no date provided or null/undefined, use current time
    if (!date) {
      date = new Date();
    }
    // If date is a string, parse it as local time in user's timezone
    else if (typeof date === 'string') {
      // If it's an ISO timestamp without timezone, treat it as UTC
      if (date.includes('T') && !date.includes('Z') && !date.includes('+') && !date.includes('-', 10)) {
        date = new Date(date + 'Z'); // Add Z to treat as UTC
      } else {
        date = new Date(date);
      }
    }
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone
    });
  };

  const loadChatHistory = async (userGuid) => {
    try {
      // Only load if we have a userGuid
      if (!userGuid) {
        return;
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await api.post(`/api/chat-history/load`, {
        user_guid: userGuid
      }, {
        timeout: 300000 // 1 minute timeout for chat history load
      });
      
      if (response.data.success && response.data.chat_history) {
        // Format timestamps for display
        const formattedMessages = response.data.chat_history.map(message => ({
          ...message,
          timestamp: formatTimestamp(message.timestamp)
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      // Don't show error to user, just start with empty chat
    }
  };

  // Load recommendation history for a user
  const loadRecommendationHistory = async (userGuid) => {
    try {
      // Only load if we have a userGuid
      if (!userGuid || userGuid === 'null' || userGuid === '') {
        return;
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await api.get(`/api/recommendations/history/${userGuid}`, {
        timeout: 300000 // 1 minute timeout for recommendation history
      });

      if (response.data && response.data.recommendation_history) {
        setRecommendationHistory(response.data.recommendation_history);
        setGroupsInitialized(false); // Reset initialization flag
      }
    } catch (error) {
      // Don't show error to user, just start with empty history
    }
  };

  // Load profile information for a user
  const loadProfileInfo = async (userGuid) => {
    try {
      if (!userGuid || userGuid === 'null' || userGuid === '') {
        return;
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await api.get(`/api/user/profile/${userGuid}`, {
        timeout: 300000 // 1 minute timeout for user profile
      });

      if (response.data && response.data.profileInfo) {
        setProfileInfo(response.data.profileInfo);
      }
      
      if (response.data && response.data.language) {
        setSpeechLanguage(response.data.language);
      }
    } catch (error) {
      // Don't show error to user, just start with empty profile
    }
  };

  // Check scenario_running status from backend
  const checkScenarioStatus = async (userGuid) => {
    try {
      if (!userGuid || userGuid === 'null' || userGuid === '') {
        return 'no';
      }
      
      const response = await api.get(`/api/scenario-status/${userGuid}`, {
        timeout: 10000 // 10 second timeout for status check
      });

      const status = response.data?.scenario_running || 'no';
      setScenarioRunning(status);
      return status;
    } catch (error) {
      console.error('Error checking scenario status:', error);
      // Default to 'no' if there's an error
      setScenarioRunning('no');
      return 'no';
    }
  };

  // Save profile information
  const saveProfileInfo = async (userGuid, profileData, language = null) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await api.post(`/api/user/profile`, {
        user_guid: userGuid,
        profileInfo: profileData,
        language: language || speechLanguage
      }, {
        timeout: 300000 // 1 minute timeout for user profile save
      });

      if (response.data.success) {
        setProfileInfo(profileData);
        if (language) {
          setSpeechLanguage(language);
        }
        
        // Update userGuid if backend generated a new one
        if (response.data.user_guid) {
          setUserGuid(response.data.user_guid);
          localStorage.setItem('user_guid', response.data.user_guid);
        }
        
        return true;
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      return false;
    }
  };

  const saveChatHistory = async (userGuid, chatMessages) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      // Only save if we have messages and a valid userGuid (or null for auto-generation)
      if (!chatMessages || chatMessages.length === 0) {
        return;
      }
      
      // Remove timestamps from messages - let database handle them
      const messagesWithoutTimestamps = chatMessages.map(message => ({
        id: message.id,
        type: message.type,
        content: message.content,
        isTyping: message.isTyping
      }));
      
      const requestData = {
        chat_history: messagesWithoutTimestamps
      };
      
      // Only include user_guid if we have one (let backend generate if null)
      if (userGuid) {
        requestData.user_guid = userGuid;
      }
           
      const response = await api.post(`/api/chat-history/save`, requestData, {
        timeout: 300000 // 1 minute timeout for chat history save
      });
      
      if (response.data.success) {       
        // If the backend returned a new user_guid (auto-generated), update our state and localStorage
        if (response.data.user_guid && response.data.user_guid !== userGuid) {
          setUserGuid(response.data.user_guid);
          localStorage.setItem('user_guid', response.data.user_guid);
        }
      }
    } catch (error) {
      console.error('Error saving chat history:', error);
      console.error('Error details:', error.response?.data);
      // Don't show error to user, just log it
    }
  };

  // Shared function for processing scenario generation
  const processScenarioGeneration = async (inputText) => {
    if (!inputText.trim()) return;

    // Check scenario status immediately before ANY processing
    if (userGuid && userGuid !== 'null' && userGuid !== '') {
      const currentStatus = await checkScenarioStatus(userGuid);
      if (currentStatus === 'yes') {
        // Don't process anything, just show the processing message
        return;
      }
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputText,
      timestamp: formatTimestamp(),
      isTyping: false
    };

    setMessages(prev => [...prev, userMessage]);
    setUserInput('');
    setLoading(true);
    setError('');
    setProcessingProgress(0);
    setForceLandingPage(false); // Reset force landing page when starting new conversation
    setScenarioRunning('yes'); // Set scenario as running locally
    
    // Scroll to bottom immediately after adding user message
    setTimeout(() => {
      if (chatMessagesRef.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
    }, 50);
    
    // Stop microphone when starting recommendation generation
    if (isListening) {
      stopListening();
    }

    try {    
      // Prepare chat history in the required format
      const chatHistory = messages.map(msg => {
        if (msg.type === 'user') {
          return { user: msg.content };
        } else {
          return { bot: msg.content };
        }
      });
      
      // Call conversation endpoint
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const conversationResponse = await api.post(`/api/conversation`, {
        user_guid: userGuid,
        user_input: inputText,
        chat_history: chatHistory
      }, {
        timeout: 300000 // 1 minute timeout for conversation
      });

      const { classification_type, response: botResponse, response_title } = conversationResponse.data;
   
      // Add the current input to scenario inputs tracking only for scenario_generation
      const updatedScenarioInputs = [...scenarioInputs, `question: ${inputText}`];
      setScenarioInputs(updatedScenarioInputs);
      
      // If classification is scenario_generation, don't show the response text
      if (classification_type === 'scenario_generation') {
        // Store response_title for display in progress-wait-message
        setResponseTitle(response_title || 'Please wait while I generate the recommendations');
        // Start processing scenario
        setIsProcessingScenario(true);
        setProgressStep(0);
        // Use response_title if available, otherwise fallback to first progress step
        const initialProgressMessage = response_title || progressSteps[0];
        updateProgress(initialProgressMessage);
        
        // Auto-scroll to show the initial progress message
        setTimeout(() => {
          if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
          }
        }, 100);
        
        // Set up progress updates with delays (skip first message as it's already shown)
        const progressDelays = [2000, 4000, 6000, 8000, 10000];
        
        const progressTimeouts = progressSteps.slice(1).map((message, index) => 
          setTimeout(() => {
            updateProgress(message);
            // Auto-scroll to show the progress message
            setTimeout(() => {
              if (chatMessagesRef.current) {
                chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
              }
            }, 100);
          }, progressDelays[index])
        );

        try {
          // Combine all scenario inputs into a single string (only scenario_generation inputs)
          const combinedScenarioInput = updatedScenarioInputs.join('; ');
          
          const recommendationsResponse = await api.post(`/api/recommendations?user=${encodeURIComponent(userGuid)}&new_scenario_group=${isNewScenarioGroup}`, {
            scenario: combinedScenarioInput,
            latest_user_input: inputText,
            profile_info: profileInfo || ''
          }, {
            timeout: 120000 // 2 minutes timeout for recommendations
          });
          
          // Clear all progress timeouts
          progressTimeouts.forEach(timeout => clearTimeout(timeout));

          // Check if the response was successful
          if (recommendationsResponse.data.success) {
            // Mark conversation as started only after successful response
            setHasStartedConversation(true);
            setResults(recommendationsResponse.data);
            setShowHistory(false); // Always show new results, not history
            
            // Handle new user_guid if one was generated
            if (recommendationsResponse.data.user_guid && !userGuid) {
              setUserGuid(recommendationsResponse.data.user_guid);
              localStorage.setItem('user_guid', recommendationsResponse.data.user_guid);
              
              // If we have profile info and this is a new user, save the profile with the new user_guid
              if (profileInfo && profileInfo.trim()) {
                saveProfileInfo(recommendationsResponse.data.user_guid, profileInfo);
              }
            }
            
            // Refresh recommendation history after generating new recommendations
            const currentUserGuid = recommendationsResponse.data.user_guid || userGuid;
            if (currentUserGuid) {
              loadRecommendationHistory(currentUserGuid);
            }
            
            // Add dynamic analysis message from outcomes, fallback to generic message
            const analysisMessage = recommendationsResponse.data.outcomes?.analysis || 
              "I've analyzed your situation and updated the plan. These scenarios are tailored to your specific caregiving situation and include actionable steps to help you make informed decisions.";
            addMessageToQueue('assistant', analysisMessage);
          } else {
            // Handle error case - show error message and don't update results
            const errorMessage = recommendationsResponse.data.message || 'Unable to generate recommendations at this time. Please try again later.';
            addMessageToQueue('assistant', errorMessage);
          }
          
          // Mark the last step as completed before clearing
          setProgressStep(progressSteps.length); // This will make all boxes completed
          
          // Clear processing state with a delay to show the last box as completed
          setTimeout(() => {
            setIsProcessingScenario(false);
            setProgressMessage('');
            setResponseTitle('');
            setScenarioRunning('no'); // Reset scenario running flag
            setIsNewScenarioGroup(false); // Reset new scenario group flag
          }, 500); // Half second delay to show last box as completed
         
        } catch (err) {
          console.error('Recommendations API Error:', err);
          const errorMessage = err.response?.data?.detail || 'An error occurred while generating recommendations';
          setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
          setIsProcessingScenario(false);
          setProgressMessage('');
          setResponseTitle('');
          setScenarioRunning('no'); // Reset scenario running flag
          // Don't reset progressStep - keep completed boxes green
          // Clear any remaining progress timeouts
          progressTimeouts.forEach(timeout => clearTimeout(timeout));
        }
      } else {
        // For general conversation, show the full bot response
        setHasStartedConversation(true);
        addMessageToQueue('assistant', botResponse);
      }
    } catch (err) {
      console.error('Conversation API Error:', err);
      const errorMessage = err.response?.data?.detail || 'An error occurred while processing your request';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
      setScenarioRunning('no'); // Reset scenario running flag
    }
  };

  const handlePresetScenario = async (scenario) => {
    // Don't set userInput - go directly to processing
    processScenarioGeneration(scenario.prompt);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    processScenarioGeneration(userInput);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleProfileChange = (value) => {
    setProfileInfo(value);
  };

  const handleProfileSave = async () => {
    const success = await saveProfileInfo(userGuid, profileInfo, speechLanguage);
    if (success) {
      setShowProfileModal(false);
      // Set focus to appropriate chat input after profile update
      setTimeout(() => {
        if (chatInputRef.current) {
          chatInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleProfileCancel = () => {
    setShowProfileModal(false);
    // Reset to saved profile data
    loadProfileInfo(userGuid);
  };

  const handleStartNewScenario = async () => {
    // Set flag for new scenario group
    setIsNewScenarioGroup(true);
    
    // Load profile info from current user before clearing
    const currentUserGuid = userGuid || localStorage.getItem('user_guid');
    if (currentUserGuid && currentUserGuid !== 'null' && currentUserGuid !== '') {
      await loadProfileInfo(currentUserGuid);
    }
    
    // Preserve profile info and language settings before clearing user data
    const currentProfileInfo = profileInfo;
    const currentLanguage = speechLanguage;
    // Don't remove user_guid from localStorage - keep user session
    setUserGuid(currentUserGuid); // Keep the current user_guid
    // Don't clear recommendation history - we want to show Plan History panel
    // setRecommendationHistory([]);
    // Keep messages - don't clear chat history
    // setMessages([]); // Keep chat history
    setResults(null); // Clear previous results to show Plan History
    setForceLandingPage(true); // Force return to landing page
    setScenarioInputs([]); // Reset scenario inputs for fresh start
    
    // Keep profile info and language settings for potential reuse
    if (currentProfileInfo) {
      setProfileInfo(currentProfileInfo);
    }
    if (currentLanguage) {
      setSpeechLanguage(currentLanguage);
    }
    
    // Generate new random scenarios
    setPresetScenarios(getRandomScenarios());
  };

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        setIsListening(false);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      setIsListening(false); // Set flag first to prevent auto-restart
      recognitionRef.current.stop();
    }
  };


  const handleLanguageChange = (e) => {
    setSpeechLanguage(e.target.value);
  };

  // Function to check supported languages
  const checkSupportedLanguages = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
           
      // Test a few Chinese variants
      const testLanguages = ['zh-CN', 'zh-HK', 'zh-TW', 'yue-HK'];
      testLanguages.forEach(lang => {
        try {
          recognition.lang = lang;
        } catch (error) {
          console.log(`❌ Language ${lang} not supported:`, error.message);
        }
      });
    }
  };

  // Show login screen if not logged in
  if (!isLoggedIn) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-logo-title">
              <img 
                src="data:image/svg+xml,%3csvg%20width='81'%20height='81'%20viewBox='0%200%2081%2081'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3ccircle%20cx='40.1228'%20cy='40.5273'%20r='40'%20fill='%233A6D8C'/%3e%3cpath%20d='M32.2033%2036.1175C27.3364%2036.1175%2023.3898%2040.0641%2023.3898%2044.9341H48.0393C52.9093%2044.9341%2056.8559%2040.9875%2056.8559%2036.1175H32.2033ZM48.0393%2048.3774H37.972C37.972%2050.8108%2038.9571%2053.0157%2040.5537%2054.6123C42.1471%2056.2058%2044.3521%2057.194%2046.7855%2057.194H56.8559C56.8559%2052.324%2052.9093%2048.3774%2048.0393%2048.3774ZM56.8559%2032.6742H32.2033C27.3364%2032.6742%2023.3898%2028.7276%2023.3898%2023.8607H48.0393C52.9093%2023.8607%2056.8559%2027.8073%2056.8559%2032.6742Z'%20fill='white'/%3e%3c/svg%3e" 
                alt="Logo" 
                className="header-logo"
              />
              <h1>Caregiving Planning Agent</h1>
            </div>
            <p>Get personalized advice for your caregiving situation</p>
          </div>
          <div className="header-right">
            {isLoggedIn && (
              <div className="user-info">
                <span className="user-email">{userFirstName}</span>
                <button 
                  className="logout-btn"
                  onClick={handleLogout}
                  title="Logout"
                >
                  <i className="fas fa-sign-out-alt"></i>
                </button>
              </div>
            )}
            {messages.length > 0 && (
              <button 
                className={`new-scenario-icon-btn ${loading || scenarioRunning === 'yes' ? 'disabled' : ''}`}
                onClick={handleStartNewScenario}
                disabled={loading || scenarioRunning === 'yes'}
                title={loading || scenarioRunning === 'yes' ? "Please wait for recommendations to complete" : "Start a New Scenario"}
              >
                <i className="fas fa-plus"></i>
              </button>
            )}
            {recommendationHistory.length > 0 && (
              <button 
                className={`plan-history-icon-btn ${showHistory ? 'active' : ''} ${loading || scenarioRunning === 'yes' ? 'disabled' : ''}`}
                onClick={() => {
                  setShowHistory(true);
                  setForceLandingPage(false); // Force chat interface to show
                }}
                disabled={loading || scenarioRunning === 'yes'}
                title={loading || scenarioRunning === 'yes' ? "Please wait for recommendations to complete" : "Plan History"}
              >
                <i className="fas fa-history"></i>
              </button>
            )}
            <button 
              className={`config-icon-btn ${loading || scenarioRunning === 'yes' ? 'disabled' : ''}`}
              onClick={() => setShowProfileModal(true)}
              disabled={loading || scenarioRunning === 'yes'}
              title={loading || scenarioRunning === 'yes' ? "Please wait for recommendations to complete" : "Profile Settings"}
            >
              <i className="fas fa-cog"></i>
            </button>
          </div>
        </div>
      </header>

      <main className="App-main">
        {!hasStartedConversation ? (
          // Initial full-screen conversation view
          <div className="initial-conversation-view">
            <div className="initial-welcome">
              <h2>Welcome to Your Caregiving Planning Assistant</h2>
              <p>I'm here to help you navigate the complexities of caregiving. Describe your specific situation or choose a scenario below.</p>
            </div>

            <div className="custom-input-section">
              <h3>Describe your caregiving situation</h3>
              {scenarioRunning === 'yes' && (
                <div className="scenario-processing-message">
                  <i className="fas fa-spinner fa-spin"></i>
                  You have an active caregiving request in progress. It should finish shortly.
                </div>
              )}
              <form onSubmit={handleSubmit} className="initial-chat-form">
                <div style={{position: 'relative'}}>
                  <textarea
                    ref={chatInputRef}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isViewingAnotherUser ? "Viewing another user's data (input disabled)" : "Tell me about your caregiving situation..."}
                    disabled={isViewingAnotherUser || loading || scenarioRunning === 'yes'}
                    className="initial-chat-input"
                    rows="3"
                    style={{paddingRight: '4rem'}}
                  />
                  <button
                    type="button"
                    className={`mic-button mic-button-landing ${isListening ? 'listening' : ''} ${!speechSupported ? 'disabled' : ''}`}
                    onClick={isListening ? stopListening : startListening}
                    disabled={loading || !speechSupported || scenarioRunning === 'yes'}
                    title={!speechSupported ? 'Voice input not supported in this browser' : scenarioRunning === 'yes' ? 'Please wait for recommendations to complete' : (isListening ? 'Stop recording' : 'Start voice input')}
                  >
                    <i className={`fas ${isListening ? 'fa-stop' : 'fa-microphone'}`}></i>
                  </button>
                </div>
                <button 
                  type="submit" 
                  disabled={loading || !userInput.trim() || scenarioRunning === 'yes'}
                  className="initial-chat-submit"
                >
                  <i className="fas fa-arrow-up"></i>
                  Get Recommendations
                </button>
              </form>
            </div>
            
            <div className="preset-scenarios">
              <h3>Quick Start Scenarios</h3>
              <div className="scenarios-grid">
                {presetScenarios.map((scenario) => (
                  <button
                    key={scenario.id}
                    className={`scenario-button ${scenarioRunning === 'yes' ? 'disabled' : ''}`}
                    onClick={() => handlePresetScenario(scenario)}
                    disabled={scenarioRunning === 'yes'}
                    title={scenarioRunning === 'yes' ? 'Please wait for recommendations to complete' : ''}
                  >
                    <h4>{scenario.title}</h4>
                    <p>{scenario.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          // Split layout after conversation starts
          <div className={`main-panels ${(results && results.outcomes) || recommendationHistory.length > 0 ? 'split-layout' : 'single-layout'}`}>
            {/* Conversation Panel - 30% width */}
            <div className="conversation-panel">
              <div className="conversation-header">
                <h3>Your Caregiving Situation</h3>
              </div>
              
              <div className="chat-container">
                <div className="chat-messages" ref={chatMessagesRef}>
                  {messages.length === 0 ? (
                    <div className="welcome-message">
                      <p>👋 Welcome! I'm here to help with your caregiving questions.</p>
                      <p>Tell me about your situation and I'll provide personalized recommendations.</p>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div key={message.id} className={`message ${message.type}`}>
                        <div className="message-content">
                          <p>{message.content}</p>
                          {message.isTyping && <span className="typing-cursor">|</span>}
                          <span className="message-time">{message.timestamp}</span>
                        </div>
                      </div>
                    ))
                  )}
                  {loading && (
                    <div className="message assistant">
                      <div className="message-content">
                        <div className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}
                  {isProcessingScenario && progressMessage && (
                    <div className="progress-message-container">
                      <p className="progress-wait-message">{responseTitle || 'Please wait while I generate the recommendations'}</p>
                      <div className="progress-bar" id="workbenchProgress">
                         {progressSteps.map((step, index) => (
                           <div 
                             key={index}
                             className={`progress-box ${
                               index < progressStep ? 'completed' : 
                               index === progressStep ? 'current' : ''
                             }`}
                             data-task-index={index}
                           ></div>
                         ))}
                      </div>
                      <p className="progress-message">{progressMessage}</p>
                    </div>
                  )}
                </div>
                
                <div className="chat-input-container">
                  {scenarioRunning === 'yes' && (
                    <div className="scenario-processing-message">
                      <i className="fas fa-spinner fa-spin"></i>
                      You have an active caregiving request in progress. It should finish shortly.
                    </div>
                  )}
                  <form onSubmit={handleSubmit} className="chat-form">
                    <textarea
                      ref={chatInputRef}
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={isViewingAnotherUser ? "Viewing another user's data (input disabled)" : "Ask about your caregiving situation..."}
                      disabled={isViewingAnotherUser || loading || scenarioRunning === 'yes'}
                      className="chat-input"
                      rows="2"
                    />
                    <button
                      type="button"
                      className={`mic-button mic-button-chat ${isListening ? 'listening' : ''} ${!speechSupported ? 'disabled' : ''}`}
                      onClick={isListening ? stopListening : startListening}
                      disabled={loading || !speechSupported || scenarioRunning === 'yes'}
                      title={!speechSupported ? 'Voice input not supported in this browser' : scenarioRunning === 'yes' ? 'Please wait for recommendations to complete' : (isListening ? 'Stop recording' : 'Start voice input')}
                    >
                      <i className={`fas ${isListening ? 'fa-stop' : 'fa-microphone'}`}></i>
                    </button>
                    <button 
                      type="submit" 
                      disabled={loading || !userInput.trim() || scenarioRunning === 'yes'}
                      className="chat-submit"
                    >
                      <i className="fas fa-arrow-up"></i>
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* Outcomes Panel - 70% width */}
            <div className="outcomes-panel">
              <div className="outcomes-header">
                <h3>Rejara Recommendations</h3>
                <div className="header-controls">

                  
                  {/* View Toggle Buttons */}
                  {!showHistory && results && results.outcomes && (results.outcomes.merged_scenarios || Object.keys(results.outcomes).length > 0) && (
                    <div className="view-toggle">
                      <button 
                        className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => setViewMode('grid')}
                      >
                        <i className="fas fa-th"></i> Grid
                      </button>
                      <button 
                        className={`toggle-btn ${viewMode === 'tabs' ? 'active' : ''}`}
                        onClick={() => setViewMode('tabs')}
                      >
                        <i className="fas fa-folder"></i> Tabs
                      </button>
                    </div>
                  )}
                </div>
              </div>
            
              <div className="outcomes-content">
                {error && (
                  <div className="error-message">
                    <h3>Error</h3>
                    <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
                  </div>
                )}
                
                {/* Recommendation History View */}
                {(showHistory || (!results && recommendationHistory.length > 0)) && (
                  <div className="recommendation-history">
                    <div className="history-header">
                      <h4>Plan History</h4>
                      {results && (
                        <button 
                          className="back-to-results-btn"
                          onClick={() => setShowHistory(false)}
                        >
                          <i className="fas fa-arrow-left"></i> Back to Current Plans
                        </button>
                      )}
                    </div>
                    
                    {/* Check if we have the new grouped structure */}
                    {recommendationHistory.length > 0 && recommendationHistory[0].scenario_group_id ? (
                      // New grouped structure
                      <div className="scenario-groups">
                        {recommendationHistory.slice().reverse().map((group, groupIndex) => {
                          const originalIndex = recommendationHistory.length - 1 - groupIndex;
                          const isExpanded = expandedGroups.has(originalIndex);
                          
                          // Use the main formatTimestamp function for consistency with chat history
                          
                          return (
                            <div key={group.scenario_group_id} className="scenario-group">
                              <div 
                                className="scenario-group-header"
                                onClick={() => {
                                  const newExpanded = new Set(expandedGroups);
                                  if (isExpanded) {
                                    newExpanded.delete(originalIndex);
                                  } else {
                                    newExpanded.add(originalIndex);
                                  }
                                  setExpandedGroups(newExpanded);
                                }}
                              >
                                <div className="scenario-group-title">
                                  <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                                  <h5>{group.scenario_title}</h5>
                                </div>
                              </div>
                              
                              {isExpanded && (
                                <div className="scenario-group-content">
                                  {group.scenarios.map((scenario, scenarioIndex) => {
                                    const scenarioKey = `${group.scenario_group_id}-${scenarioIndex}`;
                                    const isScenarioExpanded = expandedScenarios.has(scenarioKey);
                                    
                                    return (
                                      <div key={scenarioIndex} className="scenario-item">
                                        <div 
                                          className="scenario-item-header"
                                          onClick={() => {
                                            const newExpanded = new Set(expandedScenarios);
                                            if (isScenarioExpanded) {
                                              newExpanded.delete(scenarioKey);
                            } else {
                                              newExpanded.add(scenarioKey);
                                            }
                                            setExpandedScenarios(newExpanded);
                                          }}
                                        >
                                          <div className="scenario-item-title">
                                            <i className={`fas fa-chevron-${isScenarioExpanded ? 'down' : 'right'}`}></i>
                                            <div className="scenario-title-content">
                                              <h6>{scenario.main_title || 'Scenario'}</h6>
                                            </div>
                                          </div>
                                          <span className="scenario-timestamp">{formatTimestamp(scenario.timestamp)}</span>
                                        </div>
                                        
                                        {isScenarioExpanded && (
                                          <div className="scenario-recommendations">
                                            <div className="recommendations-list">
                                              {scenario.content.map((rec, recIndex) => (
                                                <div key={recIndex} className="recommendation-item">
                                                  <h6 className="recommendation-title">{rec.title}</h6>
                                                  <p className="recommendation-description">{rec.description}</p>
                                                  
                                                  {rec.considerations && rec.considerations.length > 0 && (
                                                    <div className="recommendation-considerations">
                                                      <div className="considerations-header">
                                                        <h6>Considerations:</h6>
                                                        {rec.considerations_sources && rec.considerations_sources.length > 0 && (
                                                          <button 
                                                            className="sources-button"
                                                            onClick={() => setSourcesModal({ show: true, sources: rec.considerations_sources })}
                                                          >
                                                            Sources
                                                          </button>
                                                        )}
                                                      </div>
                                                      <ul>
                                                        {rec.considerations.map((consideration, considerationIndex) => (
                                                          <li key={considerationIndex}>{consideration}</li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}
                                                  
                                                  {rec.action_steps && rec.action_steps.length > 0 && (
                                                    <div className="recommendation-actions">
                                                      <h6>Action Steps:</h6>
                                                      <ul>
                                                        {rec.action_steps.map((step, stepIndex) => (
                                                          <li key={stepIndex}>{step}</li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      // Old flat structure (fallback for migrated data)
                      <div className="history-tabs">
                        <div className="history-tabs-header">
                          {recommendationHistory.slice().reverse().map((rec, recIndex) => {
                            const originalIndex = recommendationHistory.length - 1 - recIndex;
                            const mainTitle = rec.main_title || rec.content?.[0]?.title || 'Plan';
                          
                          return (
                            <button
                              key={originalIndex}
                              className={`history-tab-button ${activeHistoryTab === originalIndex ? 'active' : ''}`}
                              onClick={() => setActiveHistoryTab(originalIndex)}
                              title={mainTitle}
                            >
                              <div className="history-tab-title">
                                {mainTitle}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {recommendationHistory.map((rec, recIndex) => (
                        <div 
                          key={recIndex} 
                          className={`history-tab-content ${activeHistoryTab === recIndex ? 'active' : ''}`}
                        >
                          <div className="recommendations-section">
                              <h5>{rec.main_title || rec.content?.[0]?.title || 'Scenario'}</h5>
                            <div className="recommendations-list">
                              {rec.content.map((scenario, scenarioIndex) => (
                                <div key={scenarioIndex} className="recommendation-item">
                                  <h6 className="recommendation-title">{scenario.title}</h6>
                                  <p className="recommendation-description">{scenario.description}</p>
                                  
                                  {scenario.considerations && scenario.considerations.length > 0 && (
                                    <div className="recommendation-considerations">
                                      <div className="considerations-header">
                                        <h6>Considerations:</h6>
                                        {scenario.considerations_sources && scenario.considerations_sources.length > 0 && (
                                          <button 
                                            className="sources-button"
                                            onClick={() => setSourcesModal({ show: true, sources: scenario.considerations_sources })}
                                          >
                                            Sources
                                          </button>
                                        )}
                                      </div>
                                      <ul>
                                        {scenario.considerations.map((consideration, considerationIndex) => (
                                          <li key={considerationIndex}>{consideration}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  
                                  {scenario.action_steps && scenario.action_steps.length > 0 && (
                                    <div className="recommendation-actions">
                                      <h6>Action Steps:</h6>
                                      <ul>
                                        {scenario.action_steps.map((step, stepIndex) => (
                                          <li key={stepIndex}>{step}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                )}

                {!showHistory && (results && (results.outcomes || results.dimensions_generated) || recommendationHistory.length > 0) ? (
                   <div>
                     {/* Show current results if available, otherwise show recommendation history */}
                     {results && (results.outcomes || results.dimensions_generated) ? (
                       <>
                         {/* Check if we have merged scenarios (new structure) or provider-based scenarios (old structure) */}
                         {results.outcomes && results.outcomes.merged_scenarios ? (
                       // New merged structure
                       <>
                         {/* Analysis is kept in response but not displayed */}
                         {results.outcomes.analysis && (
                           <div className="merging-analysis" style={{ display: 'none' }}>
                             <p><strong>Analysis:</strong> {results.outcomes.analysis}</p>
                           </div>
                         )}
                         
                         {viewMode === 'grid' ? (
                           // Grid view
                           <>
                             {results.outcomes.main_title && (
                               <div className="current-plan-title">
                                 <h4>{results.outcomes.main_title}</h4>
                               </div>
                             )}
                             <div className="merged-scenarios-grid">
                               {results.outcomes.merged_scenarios && results.outcomes.merged_scenarios.length > 0 ? (
                               results.outcomes.merged_scenarios.map((scenario, index) => (
                                 <div key={index} className="scenario-card">
                                   <h5>{scenario.title}</h5>
                                                                      <div className="scenario-content">
                                     <p className="description">
                                       {scenario.description}
                                     </p>
                                     <div className="scenario-details">
                                       <div className="considerations">
                                         <div className="considerations-header">
                                           <h6>Considerations:</h6>
                                           {scenario.considerations_sources && scenario.considerations_sources.length > 0 && (
                                             <button 
                                               className="sources-button"
                                               onClick={() => setSourcesModal({ show: true, sources: scenario.considerations_sources })}
                                             >
                                               Sources
                                             </button>
                                           )}
                                         </div>
                                         {scenario.considerations && scenario.considerations.length > 0 ? (
                                           <ul>
                                             {scenario.considerations.map((consideration, considerationIndex) => (
                                               <li key={considerationIndex}>
                                                 {consideration}
                                               </li>
                                             ))}
                                           </ul>
                                         ) : (
                                           <p>No considerations available</p>
                                         )}
                                       </div>
                                       
                                       <div className="action-steps">
                                         <h6>Action Steps:</h6>
                                         {scenario.action_steps && scenario.action_steps.length > 0 ? (
                                           <ul>
                                             {scenario.action_steps.map((step, stepIndex) => (
                                               <li key={stepIndex}>
                                                 {step}
                                               </li>
                                             ))}
                                           </ul>
                                         ) : (
                                           <p>No action steps available</p>
                                         )}
                                       </div>
                                     </div>
                                   </div>
                                 </div>
                               ))
                             ) : (
                               <div className="no-scenarios">
                                 <p>No merged scenarios available.</p>

                               </div>
                             )}
                             </div>
                           </>
                         ) : (
                           // Tab view
                           <div className="scenarios-tabs">
                             {results.outcomes.main_title && (
                               <div className="current-plan-title">
                                 <h4>{results.outcomes.main_title}</h4>
                               </div>
                             )}
                             {results.outcomes.merged_scenarios && results.outcomes.merged_scenarios.length > 0 ? (
                               <>
                                 <div className="tabs-header">
                                   {results.outcomes.merged_scenarios.map((scenario, index) => (
                                     <button
                                       key={index}
                                       className={`tab-button ${activeTab === index ? 'active' : ''}`}
                                       onClick={() => setActiveTab(index)}
                                       title={scenario.title}
                                     >
                                       {scenario.title.length > 20 ? `${scenario.title.substring(0, 20)}...` : scenario.title}
                                     </button>
                                   ))}
                                 </div>
                                 <div className="tab-content">
                                   {results.outcomes.merged_scenarios[activeTab] && (
                                     <div className="scenario-card">
                                       <h5>{results.outcomes.merged_scenarios[activeTab].title}</h5>

                                       <div className="scenario-content">
                                         <p className="description">
                                           {results.outcomes.merged_scenarios[activeTab].description}
                                         </p>
                                         <div className="scenario-details">
                                           <div className="considerations">
                                             <div className="considerations-header">
                                               <h6>Considerations:</h6>
                                               {results.outcomes.merged_scenarios[activeTab].considerations_sources && results.outcomes.merged_scenarios[activeTab].considerations_sources.length > 0 && (
                                                 <button 
                                                   className="sources-button"
                                                   onClick={() => setSourcesModal({ show: true, sources: results.outcomes.merged_scenarios[activeTab].considerations_sources })}
                                                 >
                                                   Sources
                                                 </button>
                                               )}
                                             </div>
                                             {results.outcomes.merged_scenarios[activeTab].considerations && results.outcomes.merged_scenarios[activeTab].considerations.length > 0 ? (
                                               <ul>
                                                 {results.outcomes.merged_scenarios[activeTab].considerations.map((consideration, considerationIndex) => (
                                                   <li key={considerationIndex}>
                                                     {consideration}
                                                   </li>
                                                 ))}
                                               </ul>
                                             ) : (
                                               <p>No considerations available</p>
                                             )}
                                           </div>
                                           
                                           <div className="action-steps">
                                             <h6>Action Steps:</h6>
                                             {results.outcomes.merged_scenarios[activeTab].action_steps && results.outcomes.merged_scenarios[activeTab].action_steps.length > 0 ? (
                                               <ul>
                                                 {results.outcomes.merged_scenarios[activeTab].action_steps.map((step, stepIndex) => (
                                                   <li key={stepIndex}>
                                                     {step}
                                                   </li>
                                                 ))}
                                               </ul>
                                             ) : (
                                               <p>No action steps available</p>
                                             )}
                                           </div>
                                         </div>
                                       </div>
                                     </div>
                                   )}
                                 </div>
                               </>
                             ) : (
                               <div className="no-scenarios">
                                 <p>No merged scenarios available.</p>

                               </div>
                             )}
                           </div>
                         )}
                       </>
                     ) : results.outcomes && Object.keys(results.outcomes).length > 0 ? (
                       // Fallback to old provider-based structure
                       Object.entries(results.outcomes).map(([provider, outcome]) => (
                         <div key={provider}>
                           <h4 className="provider-title">
                             {provider.toUpperCase()} - {outcome.model}
                           </h4>
                           <div className="scenarios-list">
                             {outcome.scenarios && outcome.scenarios.length > 0 ? (
                               outcome.scenarios.map((scenario, index) => (
                                 <div key={index} className="scenario-card">
                                   <h5>{scenario.title}</h5>
                                   <p className="description">
                                     {scenario.description}
                                   </p>
                                   
                                   <div className="considerations">
                                     <div className="considerations-header">
                                       <h6>Considerations:</h6>
                                       {scenario.considerations_sources && scenario.considerations_sources.length > 0 && (
                                         <button 
                                           className="sources-button"
                                           onClick={() => setSourcesModal({ show: true, sources: scenario.considerations_sources })}
                                         >
                                           Sources
                                         </button>
                                       )}
                                     </div>
                                     {scenario.considerations && scenario.considerations.length > 0 ? (
                                       <ul>
                                         {scenario.considerations.map((consideration, considerationIndex) => (
                                           <li key={considerationIndex}>
                                             {consideration}
                                           </li>
                                         ))}
                                       </ul>
                                     ) : (
                                       <p>No considerations available</p>
                                     )}
                                   </div>
                                   
                                   <div className="action-steps">
                                     <h6>Action Steps:</h6>
                                     {scenario.action_steps && scenario.action_steps.length > 0 ? (
                                       <ul>
                                         {scenario.action_steps.map((step, stepIndex) => (
                                           <li key={stepIndex}>
                                             {step}
                                           </li>
                                         ))}
                                       </ul>
                                     ) : (
                                       <p>No action steps available</p>
                                     )}
                                   </div>
                                 </div>
                               ))
                             ) : (
                               <div className="no-scenarios">
                                 <p>No scenarios available from this provider.</p>
                               </div>
                             )}
                           </div>
                         </div>
                       ))
                     ) : (
                       <div className="empty-outcomes">
                         <p>No scenarios found in results.</p>
                         <p>Results: {JSON.stringify(results, null, 2)}</p>
                       </div>
                     )}
                       </>
                     ) : null}
                   </div>
                ) : !showHistory && recommendationHistory.length === 0 ? (
                  <div className="empty-outcomes">
                    <p>No previous recommendations found. Start a new scenario to generate recommendations.</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="profile-modal-overlay" onClick={handleProfileCancel}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <h3>Profile Settings</h3>
              <button 
                className="profile-modal-close"
                onClick={handleProfileCancel}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="profile-modal-content">
              <div className="profile-form">
                <div className="form-group">
                  <label htmlFor="profile-text">Your Profile Information</label>
                  <textarea
                    id="profile-text"
                    value={profileInfo}
                    onChange={(e) => handleProfileChange(e.target.value)}
                    placeholder="Enter your profile information here. Include details about yourself, your caregiving situation, experience level, relationship to the care recipient, location, and any other relevant information that would help personalize your recommendations..."
                    rows="12"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="language-select">Voice Input Language</label>
                  <select
                    id="language-select"
                    value={speechLanguage}
                    onChange={handleLanguageChange}
                    className="language-selector-profile"
                    title="Select language for voice input"
                  >
                    {availableLanguages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="profile-modal-footer">
              <button 
                className="profile-cancel-btn"
                onClick={handleProfileCancel}
              >
                Cancel
              </button>
              <button 
                className="profile-save-btn"
                onClick={handleProfileSave}
              >
                Save Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sources Modal */}
      {sourcesModal.show && (
        <div className="sources-modal-overlay" onClick={() => setSourcesModal({ show: false, sources: [] })}>
          <div className="sources-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="sources-modal-header">
              <h6>Sources</h6>
              <button 
                className="sources-modal-close"
                onClick={() => setSourcesModal({ show: false, sources: [] })}
              >
                ×
              </button>
            </div>
            <div className="sources-modal-body">
              {sourcesModal.sources && sourcesModal.sources.length > 0 ? (
                <ul>
                  {sourcesModal.sources.map((source, index) => {
                    const colonIndex = source.indexOf(':');
                    if (colonIndex > 0) {
                      const beforeColon = source.substring(0, colonIndex);
                      const afterColon = source.substring(colonIndex + 1).trim();
                      return (
                        <li key={index}>
                          <strong>{beforeColon}:</strong>
                          <span className="source-url">{afterColon}</span>
                        </li>
                      );
                    }
                    return (
                      <li key={index}>
                        {source}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>No sources available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;